import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { ImportarPessoaService } from '../../../../../shared/services/apis/orquestrador-pessoa/importar-pessoa.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ImportarArquivoRequest } from '../../../../../shared/services/requests/pessoa/importar-arquivo.request';
import { ValidacaoImportacaoResponse } from '../../../../../shared/services/responses/pessoa/validacao-importacao.response';
import { ItemValidacaoStruct } from '../../../../../shared/services/structs/pessoa/item-validacao.struct';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AcaoImportacaoEnum } from '../../../../../shared/enums/pessoa/acao-importacao.enum';

// Tipo para controlar o filtro ativo visualmente
type FilterType = 'todos' | 'novos' | 'atualizados' | 'avisos' | 'erros';

@Component({
  selector: 'app-dialog-importar-pessoas',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatProgressBarModule,
    HttpClientModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './dialog-importar-pessoas.component.html',
  styleUrls: ['./dialog-importar-pessoas.component.css']
})
export class DialogImportarPessoasComponent implements OnInit {

  public selectedFile: File | null = null;
  public errorMessage: string | null = null;
  public MAX_SIZE_MB = 2;

  public step: number = 1;
  public isLoading: boolean = false;

  // Dados de Validação
  public validationResponse: ValidacaoImportacaoResponse | null = null;

  // Lista filtrada que será exibida na tabela
  public itemsFiltered: ItemValidacaoStruct[] = [];
  public currentFilter: FilterType = 'todos';

  public _acaoImportacaoEnum = AcaoImportacaoEnum;

  public dataSourceExample: any[] = [];

  public allColumns: string[] = [
    'id', 'codigo', 'nome', 'fantasia', 'endereco', 'numero', 'complemento',
    'bairro', 'cep', 'cidade', 'estado', 'fone', 'email', 'website',
    'tipoPessoa', 'cpf', 'cnpj', 'docEstrangeiro', 'ie', 'im',
    'situacao', 'observacoes', 'dataNascimento', 'contribuinteIbsCbs',
    'consumidorFinal', 'emailNFe', 'tiposContatos', 'contribuinte',
    'crt', 'limiteCredito', 'inscricaoSuframa', 'vendedorResponsavel'
  ];

  public displayedColumnsExample: string[] = [...this.allColumns];
  public displayedColumnsPreview: string[] = ['status', 'linha', ...this.allColumns];

  constructor(
    public dialogRef: MatDialogRef<DialogImportarPessoasComponent>,
    private importarPessoaService: ImportarPessoaService,
    private alertService: AlertService,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.carregarExemploCSV();
  }

  // --- Carregamento do CSV de Exemplo ---
  carregarExemploCSV(): void {
    this.http.get('assets/files/exemplo_planilha_pessoa.csv', { responseType: 'text' })
      .subscribe({
        next: (data) => { this.parseCSV(data); },
        error: (err) => { console.error('Erro ao carregar CSV de exemplo', err); }
      });
  }

  parseCSV(data: string): void {
    const lines = data.split('\n');
    if (!lines || lines.length < 2) return;

    const result = [];
    for (let i = 1; i < Math.min(lines.length, 6); i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const cols = line.split(';');

      const obj: any = {
        id: cols[0],
        codigo: cols[1],
        nome: cols[2],
        fantasia: cols[3],
        endereco: cols[4],
        numero: cols[5],
        complemento: cols[6],
        bairro: cols[7],
        cep: cols[8],
        cidade: cols[9],
        estado: cols[10],
        fone: cols[11],
        email: cols[12],
        website: cols[13],
        tipoPessoa: cols[14],
        cpf: cols[15],
        cnpj: cols[16],
        docEstrangeiro: cols[17],
        ie: cols[18],
        im: cols[19],
        situacao: cols[20],
        observacoes: cols[21],
        dataNascimento: cols[22],
        contribuinteIbsCbs: cols[23],
        consumidorFinal: cols[24],
        emailNFe: cols[25],
        tiposContatos: cols[26],
        contribuinte: cols[27],
        crt: cols[28],
        limiteCredito: cols[29],
        inscricaoSuframa: cols[30],
        vendedorResponsavel: cols[31]
      };
      result.push(obj);
    }
    this.dataSourceExample = result;
  }

  // --- Step 1: Upload ---
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    this.processFile(file);
    event.target.value = '';
  }

  processFile(file: File | undefined): void {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.errorMessage = 'Apenas arquivos .csv são permitidos.';
      this.selectedFile = null;
      return;
    }

    if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
      this.errorMessage = `O arquivo deve ter no máximo ${this.MAX_SIZE_MB}MB.`;
      this.selectedFile = null;
      return;
    }

    this.errorMessage = null;
    this.selectedFile = file;
    this.processarArquivo();
  }

  downloadModelo(): void {
    const link = document.createElement('a');
    link.href = 'assets/files/exemplo_planilha_pessoa.csv';
    link.download = 'exemplo_planilha_pessoa.csv';
    link.click();
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  voltarParaUpload(): void {
    this.step = 1;
    this.selectedFile = null;
    this.validationResponse = null;
    this.errorMessage = null;
    this.itemsFiltered = []; // Limpa lista
  }

  // --- Step 2: Validação e Lógica ---
  processarArquivo(): void {
    if (!this.selectedFile) return;

    this.isLoading = true;
    const reader = new FileReader();
    reader.readAsDataURL(this.selectedFile);

    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      this.enviarParaValidacao(base64);
    };

    reader.onerror = (error) => {
      this.isLoading = false;
      this.alertService.show('Erro', 'Erro ao ler arquivo.', AlertType.error);
    };
  }

  private enviarParaValidacao(base64: string): void {
    const request = new ImportarArquivoRequest();
    request.arquivoBase64 = base64;

    this.importarPessoaService.validar(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro na validação', response.errorDescription, AlertType.error);
          this.selectedFile = null;
        } else {
          this.validationResponse = response;
          this.step = 2;
          // Inicializa o filtro com 'todos' para popular a tabela imediatamente
          this.aplicarFiltro('todos');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.selectedFile = null;
        this.alertService.show('Erro', 'Falha ao processar arquivo.', AlertType.error);
      }
    });
  }

  // --- Filtros ---
  aplicarFiltro(tipo: FilterType): void {
    this.currentFilter = tipo;
    if (!this.validationResponse) return;

    const itens = this.validationResponse.itens;

    switch (tipo) {
      case 'todos':
        this.itemsFiltered = itens;
        break;
      case 'novos':
        this.itemsFiltered = itens.filter(i => i.acao === AcaoImportacaoEnum.Novo);
        break;
      case 'atualizados':
        this.itemsFiltered = itens.filter(i => i.acao === AcaoImportacaoEnum.Atualizar);
        break;
      case 'avisos':
        this.itemsFiltered = itens.filter(i => i.isAlerta);
        break;
      case 'erros':
        this.itemsFiltered = itens.filter(i => !i.isValido || i.erros.length > 0);
        break;
    }
  }

  confirmarImportacao(): void {
    if (this.isImportDisabled) return;

    if (this.isLoading) return;

    const itensValidos = this.validationResponse!.itens.filter(i => i.isValido);

    this.isLoading = true;
    this.importarPessoaService.confirmar(itensValidos).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro na Gravação', response.errorDescription, AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Importação finalizada com sucesso.', AlertType.success);
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao confirmar importação.', AlertType.error);
      }
    });
  }

  // --- Helpers ---
  getResumoTotal(): number { return this.validationResponse?.itens?.length || 0; }
  getResumoNovos(): number { return this.validationResponse?.resumo?.totalNovos || 0; }
  getResumoAtualizados(): number { return this.validationResponse?.resumo?.totalAtualizacoes || 0; }
  getResumoAvisos(): number { return this.validationResponse?.resumo?.totalAvisos || 0; }
  getResumoErros(): number { return this.validationResponse?.resumo?.totalErros || 0; }

  get isImportDisabled(): boolean {
    if (!this.validationResponse) return true;
    if (this.getResumoErros() > 0) return true;
    if (this.getResumoTotal() === 0) return true;
    return false;
  }

  getButtonTooltip(): string {
    if (this.getResumoErros() > 0) {
      return 'Resolva os erros e importe a planilha novamente para seguir com a importação';
    }
    return '';
  }

  getTooltip(item: ItemValidacaoStruct): string {
    const parts = [];
    if (item.erros?.length) parts.push(`ERROS: ${item.erros.join(', ')}`);
    if (item.avisos?.length) parts.push(`AVISOS: ${item.avisos.join(', ')}`);

    if (parts.length === 0) {
      if (item.acao === AcaoImportacaoEnum.Novo) return 'Novo Cadastro';
      if (item.acao === AcaoImportacaoEnum.Atualizar) return 'Atualização de Cadastro';
    }
    return parts.join('\n');
  }

  getRowClass(item: ItemValidacaoStruct): string {
    if (!item.isValido) return 'row-error';
    if (item.isAlerta) return 'row-warning';
    return '';
  }
}
